<template>
    <div :class="isActive ? 'selected' : null" class="card">
        <div class="card-header">
            <p class="hint mb-1">{{ routeRule.id }}</p>
            <div v-if="routeRule[COURIERS.routeRules.visibility] === VISIBILITY_STATUS.ARCHIVED" class="mb-3 mt-0">
                <badge type="danger">
                    Archived
                </badge>
            </div>
            <p v-if="routeRule && routeRule[COURIERS.routeRules.days]" class="mb-1">
                <strong><i class="fa fa-calendar mr-2"></i>Days Applied</strong>
            </p>
            <div v-if="routeRule && routeRule[COURIERS.routeRules.days]" class="d-flex flex-wrap">
                <div v-for="day in parseRouteRuleDays(routeRule[COURIERS.routeRules.days] || {})" :key="day.value" class="mt-2 mx-1">
                    <badge type="primary">
                        {{ day.label }}
                    </badge>
                </div>
            </div>
            <div v-else class="mt-2">
                <h6>
                    <i class="fa fa-calendar mr-2"></i>
                    Generic
                </h6>
                <p class="hint">Will be applied daily giving priority to day-specified route rules on overlapping times.</p>
            </div>
        </div>
        <div class="card-body d-flex flex-column">
            <div class="row mb-3">
                <div class="col-xl-12">
                    <collapse v-model="activeName" accordion>
                        <collapse-item name="1">
                            <template slot="title">
                                <p class="mb-0"><strong>Start Time</strong></p>
                                <p class="mb-0 ml-4">{{ routeRule[COURIERS.routeRules.start] }} UTC</p>
                            </template>
                            <table class="table table-striped">
                                <thead>
                                    <th>Zone</th>
                                    <th>Time</th>
                                </thead>
                                <tbody>
                                    <tr v-for="timezone in STORE_TIMEZONE_OPTIONS" :key="timezone.value">
                                        <td>{{ timezone.label }}</td>
                                        <td>
                                            {{
                                                DateTime.fromFormat(routeRule[COURIERS.routeRules.start], 'HH:mm', { zone: 'utc' })
                                                    .setZone(timezone.value)
                                                    .toLocaleString({ hour: '2-digit', minute: '2-digit', hour12: true })
                                            }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </collapse-item>
                    </collapse>
                </div>
                <div class="col-xl-12" :class="routeRule && routeRule[COURIERS.routeRules.days] ? null : 'mb-4'">
                    <collapse v-model="activeName" accordion>
                        <collapse-item name="2">
                            <template slot="title">
                                <p class="mb-0"><strong>End Time</strong></p>
                                <p class="mb-0 ml-4">{{ routeRule[COURIERS.routeRules.end] }} UTC</p>
                            </template>
                            <table class="table table-striped">
                                <thead>
                                    <th>Timezone</th>
                                    <th>Time</th>
                                </thead>
                                <tbody>
                                    <tr v-for="timezone in STORE_TIMEZONE_OPTIONS" :key="timezone.value">
                                        <td>{{ timezone.label }}</td>
                                        <td>
                                            {{
                                                DateTime.fromFormat(routeRule[COURIERS.routeRules.end], 'HH:mm', { zone: 'utc' })
                                                    .setZone(timezone.value)
                                                    .toLocaleString({ hour: '2-digit', minute: '2-digit', hour12: true })
                                            }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </collapse-item>
                    </collapse>
                </div>
                <div class="col-xl-12">
                    <table class="table table-sm">
                        <tbody>
                            <tr>
                                <th>Visible</th>
                                <td>
                                    <badge :type="routeRule[COURIERS.routeRules.visibility] === VISIBILITY_STATUS.VISIBLE ? 'success' : 'danger'">
                                        {{ routeRule[COURIERS.routeRules.visibility] === VISIBILITY_STATUS.VISIBLE ? 'Visible' : 'Hidden' }}
                                    </badge>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-xl-3">
                    <p class="hint mb-0">Cut off</p>
                    <h5>{{ routeRule[COURIERS.routeRules.cutoff] }} mins</h5>
                </div>
                <div class="col-xl-9">
                    <p-button
                        :disabled="loading === routeRule.id"
                        class="float-right"
                        type="primary"
                        @click="$emit('setRoute', { routeId: routeRule.id, shouldEmail: true })"
                    >
                        <i v-if="loading === routeRule.id" class="fa fa-circle-o-notch fa-spin"></i>
                        <i v-if="loading !== routeRule.id" class="fa fa-envelope mr-1"></i>
                        {{ loading === routeRule.id ? null : 'Change and Notify' }}
                    </p-button>
                    <p-button
                        :disabled="loading === routeRule.id"
                        class="float-right"
                        @click="$emit('setRoute', { routeId: routeRule.id, shouldEmail: false })"
                    >
                        <i v-if="loading === routeRule.id" class="fa fa-circle-o-notch fa-spin"></i>
                        {{ loading === routeRule.id ? null : 'Change' }}
                    </p-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Badge } from '@/components/UIComponents'
import { COURIERS } from 'db'
import { VISIBILITY_STATUS, STORE_TIMEZONE_OPTIONS, SORT_DAYS } from '@/utils/constants'
import { Collapse, CollapseItem } from 'element-ui'
import { DateTime } from 'luxon'
export default {
    components: {
        Badge,
        Collapse,
        CollapseItem
    },
    props: {
        isActive: {
            type: Boolean,
            default: false
        },
        routeRule: {
            type: Object,
            required: true
        },
        loading: {
            type: [Boolean, String],
            default: false
        }
    },
    data() {
        return {
            VISIBILITY_STATUS,
            STORE_TIMEZONE_OPTIONS,
            COURIERS,
            DateTime,
            activeName: '0'
        }
    },
    methods: {
        parseRouteRuleDays(days) {
            return Object.keys(days)
                .map((day) => ({
                    label: day.charAt(0).toUpperCase() + day.slice(1),
                    value: day
                }))
                .sort((a, b) => SORT_DAYS[a.value] - SORT_DAYS[b.value])
        }
    }
}
</script>

<style scoped lang="scss">
.card {
    th,
    td,
    p {
        color: #000;
    }
}
.selected {
    border: 2px solid #00bbff;
}
</style>
